import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import Image from 'next/image';

import { Input } from '@components/atoms';
import { formatPhoneNumber } from '@utils/phoneNumberStrings';
import { updateObjectKeyValue } from '@utils/updateObjectKeyValue';

type PhoneNumberInputProps<T> = {
  object: T;
  objectKey: keyof T;
  phoneValue: string;
  required?: boolean;
  maxWidth?: string;
  setPhoneNumber: Dispatch<SetStateAction<T>>;
};

function PhoneNumberInput<T>({
  object,
  maxWidth,
  required,
  objectKey,
  phoneValue,
  setPhoneNumber,
}: PhoneNumberInputProps<T>) {
  const onPhoneNumberChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (target.value.length < 2) {
      const newObject = updateObjectKeyValue(object, objectKey, '+1');

      return setPhoneNumber(newObject);
    }

    if (target.value.length === 3 && target.value[1] !== '1') return;

    const removeAction = target.value.length < phoneValue.length;

    if (removeAction) {
      const excludedChars = ['-', ' ', '(', ')'];
      const char = phoneValue[phoneValue.length - 1];
      const shouldSkip = excludedChars.includes(char);

      if (shouldSkip) {
        const newObject = updateObjectKeyValue(object, objectKey, target.value);

        return setPhoneNumber(newObject);
      }
    }

    const cursorPosition = target.selectionStart;
    const formattedValue = formatPhoneNumber(target.value);

    const newObject = updateObjectKeyValue(object, objectKey, formattedValue);

    setPhoneNumber(newObject);

    target.setSelectionRange(cursorPosition, cursorPosition);
  };

  return (
    <Wrapper $maxWidth={maxWidth}>
      <Input
        id="phoneInput"
        value={phoneValue}
        label="Phone Number"
        placeholder="+1 (___) ___-____"
        onChange={onPhoneNumberChange}
        padding="10px 16px 10px 54px"
        required={required}
        maxWidth={maxWidth}
      />

      <StyledImage
        height={18}
        width={24}
        loading="lazy"
        alt="usa-flag"
        src="icons/usa-flag.svg"
      />
    </Wrapper>
  );
}

const StyledImage = styled(Image)`
  position: absolute;

  top: 45px;
  left: 16px;
`;

const Wrapper = styled.div<{ $maxWidth?: string }>`
  position: relative;

  width: 100%;
  max-width: ${({ $maxWidth }) => ($maxWidth ? $maxWidth : '420px')};
`;

export default PhoneNumberInput;
